import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send the admin session cookie on every request (no-op for same-origin in most
// browsers, but explicit so cross-origin preview environments work too).
axios.defaults.withCredentials = true;

// Notify subscribers (e.g. AdminPage) when any API call returns 401 so they can
// drop their local auth state and prompt re-login.
const unauthorizedListeners = new Set();
export const onUnauthorized = (cb) => {
  unauthorizedListeners.add(cb);
  return () => unauthorizedListeners.delete(cb);
};
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      unauthorizedListeners.forEach((cb) => {
        try { cb(); } catch { /* swallow */ }
      });
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Properties
  getProperties: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const response = await axios.get(`${API}/properties?${params}`);
    return response.data;
  },

  getProperty: async (id) => {
    const response = await axios.get(`${API}/properties/${id}`);
    return response.data;
  },

  createProperty: async (data) => {
    const response = await axios.post(`${API}/properties`, data);
    return response.data;
  },

  updateProperty: async (id, data) => {
    const response = await axios.put(`${API}/properties/${id}`, data);
    return response.data;
  },

  deleteProperty: async (id) => {
    const response = await axios.delete(`${API}/properties/${id}`);
    return response.data;
  },

  // Contact
  submitContact: async (data) => {
    const response = await axios.post(`${API}/contact`, data);
    return response.data;
  },

  getContactSubmissions: async (unreadOnly = false) => {
    const response = await axios.get(`${API}/contact?unread_only=${unreadOnly}`);
    return response.data;
  },

  markSubmissionRead: async (id) => {
    const response = await axios.put(`${API}/contact/${id}/read`);
    return response.data;
  },

  // Maintenance
  submitMaintenance: async (data) => {
    const response = await axios.post(`${API}/maintenance`, data);
    return response.data;
  },

  // Maintenance with file attachments
  submitMaintenanceWithFiles: async (formData, files) => {
    // First upload the files
    let attachments = [];
    if (files && files.length > 0) {
      const uploadFormData = new FormData();
      files.forEach(file => uploadFormData.append('files', file));
      const uploadResponse = await axios.post(`${API}/upload/multiple`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (uploadResponse.data.uploaded) {
        attachments = uploadResponse.data.uploaded.map(f => `${BACKEND_URL}${f.url}`);
      }
    }
    // Then submit the maintenance request with attachment URLs
    const response = await axios.post(`${API}/maintenance`, {
      ...formData,
      attachments
    });
    return response.data;
  },

  getMaintenanceRequests: async (status = null) => {
    const url = status ? `${API}/maintenance?status=${status}` : `${API}/maintenance`;
    const response = await axios.get(url);
    return response.data;
  },

  updateMaintenanceStatus: async (id, status) => {
    const response = await axios.put(`${API}/maintenance/${id}/status?status=${status}`);
    return response.data;
  },

  // News
  getNews: async (category = null, limit = 10) => {
    let url = `${API}/news?limit=${limit}`;
    if (category) url += `&category=${category}`;
    const response = await axios.get(url);
    return response.data;
  },

  getNewsArticle: async (slug) => {
    const response = await axios.get(`${API}/news/${slug}`);
    return response.data;
  },

  generateNewsArticle: async (data) => {
    const response = await axios.post(`${API}/news/generate`, data);
    return response.data;
  },

  deleteNewsArticle: async (id) => {
    const response = await axios.delete(`${API}/news/${id}`);
    return response.data;
  },

  // Testimonials
  getTestimonials: async () => {
    const response = await axios.get(`${API}/testimonials`);
    return response.data;
  },

  createTestimonial: async (data) => {
    const response = await axios.post(`${API}/testimonials`, data);
    return response.data;
  },

  deleteTestimonial: async (id) => {
    const response = await axios.delete(`${API}/testimonials/${id}`);
    return response.data;
  },

  // Stats
  getStats: async () => {
    const response = await axios.get(`${API}/stats`);
    return response.data;
  },

  // Seed
  seedDatabase: async () => {
    const response = await axios.post(`${API}/seed`);
    return response.data;
  },

  // Health check
  healthCheck: async () => {
    const response = await axios.get(`${API}/health`);
    return response.data;
  },

  // Image Upload
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  uploadMultipleImages: async (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await axios.post(`${API}/upload/multiple`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Admin Auth
  adminLogin: async (password) => {
    const response = await axios.post(`${API}/admin/login`, { password });
    return response.data;
  },

  adminLogout: async () => {
    const response = await axios.post(`${API}/admin/logout`);
    return response.data;
  },

  adminMe: async () => {
    const response = await axios.get(`${API}/admin/me`);
    return response.data;
  },

  // Landlords
  getLandlords: async () => {
    const response = await axios.get(`${API}/landlords`);
    return response.data;
  },

  createLandlord: async (data) => {
    const response = await axios.post(`${API}/landlords`, data);
    return response.data;
  },

  deleteLandlord: async (id) => {
    const response = await axios.delete(`${API}/landlords/${id}`);
    return response.data;
  },

  landlordPortalAccess: async (email, accessCode) => {
    const response = await axios.get(`${API}/landlord/portal?email=${encodeURIComponent(email)}&access_code=${accessCode}`);
    return response.data;
  },

  // Reviews (Google Reviews manually entered)
  getReviews: async (visibleOnly = true) => {
    const response = await axios.get(`${API}/reviews?visible_only=${visibleOnly}`);
    return response.data;
  },

  // Google Reviews (live from Google Places API)
  getGoogleReviews: async () => {
    const response = await axios.get(`${API}/google-reviews`);
    return response.data;
  },

  // Sync Google reviews to database
  syncGoogleReviews: async () => {
    const response = await axios.post(`${API}/google-reviews/sync`);
    return response.data;
  },

  // Get selected/visible reviews for website display
  getSelectedReviews: async () => {
    const response = await axios.get(`${API}/reviews/selected`);
    return response.data;
  },

  // Toggle review visibility
  toggleReviewVisibility: async (id, visible) => {
    const response = await axios.patch(`${API}/reviews/${id}/visibility?visible=${visible}`);
    return response.data;
  },

  createReview: async (data) => {
    const response = await axios.post(`${API}/reviews`, data);
    return response.data;
  },

  updateReview: async (id, data) => {
    const response = await axios.put(`${API}/reviews/${id}`, data);
    return response.data;
  },

  deleteReview: async (id) => {
    const response = await axios.delete(`${API}/reviews/${id}`);
    return response.data;
  },

  // Site Content (CMS)
  getAllContent: async () => {
    const response = await axios.get(`${API}/content`);
    return response.data;
  },

  getContentSection: async (section) => {
    const response = await axios.get(`${API}/content/${section}`);
    return response.data;
  },

  updateContentSection: async (section, content) => {
    const response = await axios.put(`${API}/content/${section}`, { content });
    return response.data;
  },

  seedDefaultContent: async () => {
    const response = await axios.post(`${API}/content/seed-defaults`);
    return response.data;
  },
};

export default api;
